import { doDELETE, doGET, doPOST, doPUT } from "../../util/HttpUtil";
import {
  clearBrokerAuthStorage,
  normalizeBrokerProfile as normalizeBrokerAuthProfile,
  persistBrokerAuthResponse,
} from "../../util/brokerAuth";

const BROKER_PROFILE_STORAGE_KEY = "BrokerProfile";
export const BROKER_APPLICATION_NOTE_RECORD_TYPE = "broker-application";
// Comment author/audience type for broker admins (matches Comment.BROKER_ADMIN_TYPE
// on the backend) — kept distinct from FV Bank staff (5) so the two note streams
// never mix. FV Bank staff (type 5) still see broker notes; brokers only see theirs.
export const BROKER_ADMIN_USER_TYPE = 7;
const BROKER_NOTE_FOR_TYPE = [5, BROKER_ADMIN_USER_TYPE];

export const getBrokerProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(BROKER_PROFILE_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

export const setBrokerProfile = (profile, email = "") => {
  persistBrokerAuthResponse(profile, { email });
};

export const clearBrokerProfile = () => {
  clearBrokerAuthStorage();
};

export const BROKER_PROFILE_EVENT = "broker-profile-updated";

// Keep the cached login profile (used by the sidebar) in sync when the broker
// logo changes mid-session, and notify listeners so the chrome re-renders
// without requiring a full re-login.
export const syncBrokerProfile = (partial = {}) => {
  try {
    const profile = getBrokerProfile() || {};
    const next = normalizeBrokerAuthProfile({ ...profile, ...partial }, profile.email, profile);
    localStorage.setItem(BROKER_PROFILE_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(BROKER_PROFILE_EVENT));
  } catch {
    // ignore storage errors
  }
};

export const syncBrokerProfileLogo = (logo) => {
  const profile = getBrokerProfile() || {};
  const next = logo || "";
  if (profile.logo === next) return;
  syncBrokerProfile({ logo: next });
};

const toQuery = (params = {}) => {
  const query = new URLSearchParams();
  const unsupportedGridParams = new Set(["view", "search", "aging"]);
  Object.entries(params).forEach(([key, value]) => {
    if (unsupportedGridParams.has(key)) return;
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  return query.toString();
};

export const BrokerService = {
  captchaBypassConfig: () => doGET("/api/sys-config/defaultOTP/detail", true),
  requestLoginOtp: ({ email, recaptchaToken }) => doPOST("/auth/broker/token", {
    email,
    platform: "web",
    gCaptchaResponse: recaptchaToken,
  }),
  login: ({ email, otpId, otpValue }) => doPOST("/auth/broker/login", { email, otpId, otpValue }),
  applications: (params) => doPOST("/api/application-form/grid", {
    queryURL: toQuery(params || {}),
  }),
  statistics: (body) => doPOST("/api/application-form/statistics", body || {}),
  createApplication: (payload) => doPOST("/api/business/create", payload),
  createApplicationV2: async (payload) => {
    const { email, brokerReferenceId, type, Business_Name: businessName } = payload || {};
    const created = await doPOST("/api/business/create-empty", {
      email,
      brokerReferenceId,
      Business_Name: businessName,
      type: type == null ? 1 : type,
      formVersion: 2,
    });
    const id = created?.data?.applicationId;
    if (!id) return created;
    return { ...created, data: { ...(created.data || {}), _id: id } };
  },
  updateApplication: (id, payload) => doPUT("/api/business/update", { ...(payload || {}), _id: id }),
  submitApplication: (id) => doGET(`/api/business/submit?id=${id}&markFormSubmit=true&markDocSubmit=true`),
  detail: (id) => doGET(`/api/application-form/detail?id=${id}&withExtension=true`),
  // Assign broker-managed tags to an application (broker-scoped via userAuthCheck).
  // `version` enables optimistic-lock detection so callers can refetch + retry.
  updateBrokerTags: (id, brokerTags, version) => doPUT("/api/application-form/update", { _id: id, brokerTags, version }),
  businessDetail: (id) => doGET(`/api/business/detail?id=${id}`),
  // Per-owner KYC (Ondato) records for an application, keyed by ubo_id.
  kycStatuses: (formId) => doGET(`/api/ondato/grid?form_id=${formId}&sortBy=seq&sortAsc=false&rows=-1`),
  // Business form field schema (labels + option-value mappings) from SysConfig,
  // so detail views can derive labels/values from the form definition.
  businessFields: () => doGET("/api/cyclos/fields-json?group=Business"),
  // formVersion: 2 marks the application as V2 so the document-first individual flow
  // activates in the broker portal. Must hit /create-empty (not /create) — that is the
  // endpoint whose controller (IndividualLiteController.createEmpty) persists formVersion
  // onto the ApplicationForm, exactly as createApplicationV2 uses /api/business/create-empty.
  // /create does NOT store formVersion, so the app would stay formVersion-less → legacy flow.
  // create-empty returns { applicationId }, so surface it as _id like createApplicationV2 does.
  createIndividualApplication: async (payload) => {
    const created = await doPOST("/api/individual-lite/create-empty", { ...(payload || {}), formVersion: 2 });
    const id = created?.data?.applicationId;
    if (!id) return created;
    return { ...created, data: { ...(created.data || {}), _id: id } };
  },
  updateIndividualApplication: (id, payload) => doPUT("/api/individual-lite/update", { ...(payload || {}), _id: id }),
  submitIndividualApplication: (id) => doGET(`/api/individual-lite/submit?id=${id}&markFormSubmit=true&markDocSubmit=true`),
  individualDetail: (id) => doGET(`/api/individual-lite/detail?id=${id}`),
  individualFields: () => doGET("/api/cyclos/fields-json?group=Individuals_Lite"),
  // Broker notes use a broker-scoped record type so they do not mix with the
  // internal application-form audit/comment stream used by caas-console.
  comments: (recordOf, params = {}) => doGET(`/api/comment/grid?${toQuery({
    sortBy: "at",
    sortAsc: false,
    recordType: params.recordType || BROKER_APPLICATION_NOTE_RECORD_TYPE,
    recordOf,
    rows: params.rows ?? 25,
    page: params.page ?? 1,
  })}`),
  addComment: (recordOf, remark, recordType = BROKER_APPLICATION_NOTE_RECORD_TYPE) => doPOST("/api/comment/create", {
    recordType,
    recordOf,
    byType: BROKER_ADMIN_USER_TYPE,
    forType: BROKER_NOTE_FOR_TYPE,
    content: { remark },
  }),
  // Per-broker tag registry — reuses the shared tag-element collection, but
  // requests only broker-managed tag elements. System Application tags are
  // loaded separately so they do not appear as editable broker tags.
  brokerTagElements: () => doGET(`/api/tag-element/grid?${toQuery({
    page: 1,
    rows: -1,
    sortBy: "label",
    sortAsc: true,
    kind: "BrokerApplicationForm",
  })}`),
  applicationSystemTagElements: () => doGET(`/api/tag-element/grid?${toQuery({
    page: 1,
    rows: -1,
    sortBy: "label",
    sortAsc: true,
    kind: "Application",
    systemGenerated: true,
  })}`),
  createBrokerTagElement: (payload) => doPOST("/api/tag-element/create", { kind: "BrokerApplicationForm", ...(payload || {}) }),
  updateBrokerTagElement: (id, payload) => doPUT("/api/tag-element/update", { kind: "BrokerApplicationForm", ...(payload || {}), _id: id }),
  deleteBrokerTagElement: (id) => doDELETE(`/api/tag-element/delete?id=${id}`),
  brokerDetail: (id) => doGET(`/api/brokers/detail?id=${id}`),
  // rows=-1 returns the broker's whole team (no pagination) so admin-label
  // resolution (brokerAdmin_id -> name, used app-wide) is always complete.
  team: (brokerId) => doGET(`/api/broker-admin/grid?${toQuery({
    page: 1,
    rows: -1,
    sortBy: "createdAt",
    sortAsc: false,
    brokerId,
  })}`),
  // Server-side filtered / sorted / paginated team list. Accepts a pre-built
  // params map (separate name/email caseIgnore filters, status in[...], sort,
  // page, rows) — the backend scopes broker-admin rows to the broker itself.
  teamGrid: (params = {}) => doGET(`/api/broker-admin/grid?${toQuery(params)}`),
  addTeamAdmin: (payload) => doPOST("/api/broker-admin/create", payload),
  updateTeamAdmin: (id, payload) => doPUT("/api/broker-admin/update", { ...(payload || {}), _id: id }),
  deleteTeamAdmin: (id) => doDELETE(`/api/broker-admin/delete?id=${id}`),
};
